import { useState, useEffect, useRef } from 'react';
import {
  collection, addDoc, deleteDoc, doc,
  onSnapshot, query, orderBy, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { displayName } from '../utils/displayName';
import './Blog.scss';

const POSTS_COL = collection(db, 'blog');

const EMOJIS = ['💕', '🌸', '✈️', '🌍', '☕', '🌙', '🌅', '🎶', '📖', '🍜', '🌺', '💌', '🥂', '🌿', '🐾', '🎠'];

const AUTHOR_COLORS = {
  'rajeshmaydev@gmail.com':  {
    bg:     'color-mix(in srgb, var(--clr-p, #f4a5ae) 14%, white)',
    border: 'var(--clr-p, #f4a5ae)',
    dot:    'var(--clr-p, #f4a5ae)',
  },
  'indira02032001@gmail.com':{ bg: '#ede8fd', border: '#b9a5f4', dot: '#8a75e8' },
};
const defaultColor = { bg: '#f3f3f3', border: '#ccc', dot: '#999' };

function authorColor(email) {
  return AUTHOR_COLORS[email] ?? defaultColor;
}

function formatDate(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const Blog = () => {
  const { user } = useAuth();
  const [posts, setPosts]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [expanded, setExpanded]   = useState(null);   // postId currently open
  const [showForm, setShowForm]   = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({ title: '', content: '', emoji: '💕' });
  const contentRef = useRef(null);

  useEffect(() => {
    const q   = query(POSTS_COL, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, snap => {
      setPosts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const openForm = () => {
    setForm({ title: '', content: '', emoji: '💕' });
    setShowForm(true);
    setExpanded(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) return;
    setSubmitting(true);
    await addDoc(POSTS_COL, {
      title:       form.title.trim(),
      content:     form.content.trim(),
      emoji:       form.emoji,
      authorEmail: user.email,
      authorName:  displayName(user.email),
      createdAt:   serverTimestamp(),
    });
    setSubmitting(false);
    setShowForm(false);
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post? 🥺')) return;
    await deleteDoc(doc(db, 'blog', id));
    if (expanded === id) setExpanded(null);
  };

  return (
    <div className="blog">

      {/* ── Header ── */}
      <div className="blog-header">
        <div className="blog-header-text">
          <h2 className="blog-title">Our Little Corner 💌</h2>
          <p className="blog-subtitle">Notes, stories & sweet nothings — just for us</p>
        </div>
        <button className="btn-write" onClick={openForm}>✦ Write a note</button>
      </div>

      {/* ── Write form modal ── */}
      {showForm && (
        <div className="blog-modal-overlay" onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div className="blog-modal">
            <button className="modal-close" onClick={() => setShowForm(false)}>×</button>
            <h3 className="modal-title">A new little note ✨</h3>

            <form onSubmit={submit} className="blog-form">
              {/* Emoji picker */}
              <div className="emoji-picker">
                {EMOJIS.map(em => (
                  <button
                    key={em} type="button"
                    className={`emoji-btn ${form.emoji === em ? 'selected' : ''}`}
                    onClick={() => setForm(f => ({ ...f, emoji: em }))}
                  >{em}</button>
                ))}
              </div>

              <input
                className="form-input"
                placeholder="Give it a sweet title…"
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                maxLength={120}
                required
              />

              <textarea
                ref={contentRef}
                className="form-textarea"
                placeholder="Write anything — a memory, a thought, a love letter…"
                value={form.content}
                onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                rows={9}
                required
              />

              <div className="form-footer">
                <span className="form-author">
                  Posting as <strong>{displayName(user?.email)}</strong>
                </span>
                <button type="submit" className="btn-post" disabled={submitting}>
                  {submitting ? 'Posting…' : 'Post ♥'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Post list ── */}
      {loading && <p className="blog-empty">Loading notes… 🌸</p>}

      {!loading && posts.length === 0 && (
        <div className="blog-empty-state">
          <span className="empty-emoji">💌</span>
          <p>No notes yet — be the first to write something sweet!</p>
        </div>
      )}

      <div className="posts-grid">
        {posts.map(post => {
          const isOpen   = expanded === post.id;
          const isMine   = post.authorEmail === user?.email;
          const ac       = authorColor(post.authorEmail);
          const preview  = post.content.slice(0, 160) + (post.content.length > 160 ? '…' : '');

          return (
            <article
              key={post.id}
              className={`post-card ${isOpen ? 'open' : ''}`}
              style={{ '--author-bg': ac.bg, '--author-border': ac.border }}
            >
              <div className="post-card-top">
                <span className="post-emoji">{post.emoji}</span>
                <div className="post-meta">
                  <span className="post-author-dot" style={{ background: ac.dot }} />
                  <span className="post-author">{post.authorName}</span>
                  <span className="post-date">{formatDate(post.createdAt)}</span>
                </div>
              </div>

              <h3 className="post-title">{post.title}</h3>

              <p className="post-excerpt">
                {isOpen ? post.content : preview}
              </p>

              <div className="post-card-footer">
                {post.content.length > 160 && (
                  <button
                    className="btn-read"
                    onClick={() => setExpanded(isOpen ? null : post.id)}
                  >
                    {isOpen ? 'Show less ↑' : 'Read more ↓'}
                  </button>
                )}
                {isMine && (
                  <button className="btn-delete" onClick={() => deletePost(post.id)}>
                    delete
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default Blog;
